import { useEffect, useState } from "react";
import { connect } from "react-redux";

import { RefreshRounded } from "@mui/icons-material";
import { Grid, Typography, useTheme } from "@mui/material";
import { format } from "date-fns";
import { v4 as uuid } from "uuid";

import constants from "../../assets/constants";
import * as BotpressSessionActions from "../../state/redux/botpressSession.js";
import { formatIsoDateString } from "../../utils/helpers.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget.js";
import WidgetContent from "../ui/widgetContent.js";
import WidgetSkeleton from "../ui/widgetSkeleton.js";
import WidgetTitle from "../ui/widgetTitle.js";

const StatusIndicator = (state, theme) => {
  const getStatusColor = status => {
    const details = {
      operational: {
        main: theme.palette.success.main,
        contrastText: theme.palette.success.contrastText,
      },
      degraded_performance: {
        main: theme.palette.warning.main,
        contrastText: theme.palette.warning.contrastText,
      },
      partial_outage: {
        main: theme.palette.warning.main,
        contrastText: theme.palette.warning.contrastText,
      },
      major_outage: {
        main: theme.palette.error.main,
        contrastText: theme.palette.error.contrastText,
      },
    };

    return (
      details[status.statusText] || {
        main: status.statusColor.main ? status.statusColor.main : theme.palette.info.main,
        contrastText: status.statusColor.contrastText
          ? status.statusColor.contrastText
          : theme.palette.info.contrastText,
      }
    );
  };

  const statusColor = getStatusColor(state);

  return (
    <Grid
      key={uuid()}
      container
      width={"100%"}
      flexWrap={"wrap"}
      borderRadius={"24px"}
      alignContent={"center"}
      backgroundColor={statusColor.main}
      color={statusColor.contrastText}
      spacing={0}
      padding={0.5}
      flexDirection={"column"}
      justifySelf={"flex-start"}
      justifyContent={"center"}
      textAlign={"center"}
    >
      <Grid>
        <Typography variant={"overline"} lineHeight={"unset"}>
          {state.statusLabel}
        </Typography>
      </Grid>
      <Grid>
        <Typography variant={"h6"} textTransform={"capitalize"} lineHeight={"unset"}>
          {state.statusText}
        </Typography>
      </Grid>
    </Grid>
  );
};

const BotpressBotStatus = props => {
  const {
    widgetSize = "medium",
    minHeight = "333px",
    botStatus,
    botpressSummary,
    configurations,
  } = props;

  const theme = useTheme();

  const [botpressStatus, setBotpressStatus] = useState(null);

  useEffect(() => {
    if (botStatus && botpressSummary) {
      let configs = configurations;
      if (configs == null) {
        configs = "Configuration|Unknown";
      }

      let uxConfigs = configurations.filter(
        config => config.configSubType === constants.botpressConfigSubTypeEnums.aiModel,
      );
      let ragConfigs = configurations.filter(
        config => config.configSubType === constants.botpressConfigSubTypeEnums.ragAi,
      );

      const firstConfig = uxConfigs[0].value || "Unknown";
      const secondConfig = ragConfigs[0].value || "Unknown";

      const webchatStatus = botpressSummary.components.find(item => item.name === "Webchat");
      let myStatus = {
        name: botStatus.name,
        displayStates: [
          {
            statusColor: { main: null, contrastText: null },
            statusLabel: "Webchat Status",
            statusText: webchatStatus?.status,
          },
          {
            statusColor: { main: null, contrastText: null },
            statusLabel: "Publish Date",
            statusText: formatIsoDateString(new Date(botStatus.deployedAt)),
          },
          {
            statusColor: { main: null, contrastText: null },
            statusBackgroundColor: null,
            statusLabel: "Crawl Frequency",
            statusText:
              botStatus.recurringEvents?.weeklyCrawl?.payload?.frequency || "Manual Updates Only",
          },
          {
            statusColor: { main: null, contrastText: null },
            statusBackgroundColor: null,
            statusLabel: "Knowledge Base Update",
            statusText: formatIsoDateString(new Date(botStatus.updatedAt)),
            // statusText: format(new Date(botStatus.updatedAt), "PPpp"),
          },
          {
            statusColor: { main: null, contrastText: null },
            statusBackgroundColor: null,
            statusLabel: "UX AI / RAG AI",
            statusText: `${firstConfig} | ${secondConfig}`,
            // statusText: format(new Date(botStatus.updatedAt), "PPpp"),
          },
        ],
        updatedAt: format(new Date(), "PPpp"),
      };
      setBotpressStatus(myStatus);
    }
  }, [botStatus, botpressSummary, configurations]);

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title={props.organizationODM ? props.organizationODM.omniData.name : "Client Portal"}
        text={"Client Status"}
      >
        <OmniResponsiveButton
          tooltipTitle="Refresh Stats"
          buttonText="Refresh"
          icon={<RefreshRounded />}
          onClick={() => {
            props.loadBotRequest();
            props.loadBotpressSummaryRequest();
            setBotpressStatus(state => ({ ...state, updatedAt: format(new Date(), "PPpp") }));
          }}
        />
      </WidgetTitle>
      {!botpressStatus && <WidgetSkeleton />}
      {botpressStatus && (
        <WidgetContent>
          <Grid container flexDirection={"column"} height={"100%"} justifyContent={"space-between"}>
            <Grid container spacing={1}>
              {botpressStatus?.displayStates?.map(state => {
                return StatusIndicator(state, theme);
              })}
            </Grid>
            <Grid
              container
              flexDirection={"row"}
              justifySelf={"flex-end"}
              justifyContent={"space-between"}
            >
              <Typography variant="overline">Last Update:</Typography>
              <Typography variant="overline">{botpressStatus.updatedAt}</Typography>
            </Grid>
          </Grid>
        </WidgetContent>
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  botStatus: state.botpressSession.botStatus,
  botpressSummary: state.botpressSession.botpressSummary,
  configurations: state.botpressSession.configurations,
  organizationODM: state.userSession.organizationODM,
});

const mapDispatchToProps = {
  loadBotRequest: BotpressSessionActions.loadBotRequest,
  loadBotpressSummaryRequest: BotpressSessionActions.loadBotpressSummaryRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressBotStatus);
